import { ChevronLeft, X, Rocket, CheckCircle2, AlertTriangle, Icon, ClipboardList, Settings, Users, FileText } from "lucide-react";
import { toast } from "../../../lib/toast";
import { useSetup } from "../SetupContext";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";

interface TooltipEntry {
  name: string;
  value: number;
  color: string;
  dataKey: string;
}
interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff",
      border: "1px solid var(--border)",
      borderRadius: 8,
      padding: "10px 14px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.10)",
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)", marginBottom: 6 }}>
        {label}
      </div>
      {payload.map((p) => (
        <div
          key={p.dataKey}
          style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, marginBottom: 3 }}
        >
          <span style={{
            width: 8, height: 8, borderRadius: "50%",
            background: p.color, display: "inline-block",
          }} />
          <span style={{ color: "var(--text-secondary)" }}>{p.name}:</span>
          <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>${p.value}M</span>
        </div>
      ))}
    </div>
  );
}
export const spendTrendData = [
  { month: "Jan", confidence: 84, accuracy: 82 },
  { month: "Feb", confidence: 85, accuracy: 83 },
  { month: "Mar", confidence: 86, accuracy: 83 },
  { month: "Apr", confidence: 87, accuracy: 82 },
  { month: "May", confidence: 85, accuracy: 81 },
  { month: "Jun", confidence: 84, accuracy: 82 },
  { month: "Jul", confidence: 85, accuracy: 83 },
  { month: "Aug", confidence: 86, accuracy: 84 },
  { month: "Sep", confidence: 88, accuracy: 85 },
  { month: "Oct", confidence: 90, accuracy: 86 },
  { month: "Nov", confidence: 92, accuracy: 87 },
  { month: "Dec", confidence: 93, accuracy: 88 },
];
export function SpendTrendChart() {
  return (
    <div className="panel-card">
      <div className="border-bottom pad-bottom-10 mar-bottom-10">
        <div className="dashboard-panel-title">Classification accuracy & confidence</div>
        <div className="dashboard-panel-sub">Rolling 12 months</div>
      </div>
      <div style={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={spendTrendData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />

            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} domain={[70, 100]} />

            <Tooltip content={<CustomTooltip />} />

            <Area
              type="monotone"
              dataKey="confidence"
              stroke="#6b3fa0"
              fill="#6b3fa0"
              fillOpacity={0.15}
            />

            <Area
              type="monotone"
              dataKey="accuracy"
              stroke="#3265aa"
              fill="#3265aa"
              fillOpacity={0.08}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function GovernanceQAStep({ onLaunch, onBack, onCancel }: { onLaunch: () => void; onBack: () => void; onCancel: () => void }) {

type KpiCardProps = {
  title: string;
  value: string;
  status?: "success" | "warning" | "danger";
  icon: React.ElementType;
};

const KpiCard = ({ title, value, status, icon: Icon }: KpiCardProps) => (
  <div className="dashboard-kpi-card">
    <div className="dashboard-kpi-icon">
      <Icon size={18} />
    </div>
    <div className="dashboard-kpi-content">
      <div className="dashboard-kpi-title">{title}</div>
      <div className="dashboard-kpi-value">{value}</div>
    </div>
    {status && <span className={`dashboard-kpi-dot ${status}`} />}
  </div>
);

type QueueRowProps = {
  title: string;
  value: number;
  tag: string;
  danger?: boolean;
};

const QueueRow = ({ title, value, tag, danger }: QueueRowProps) => (
  <div className="queue-row">
    <div>
      <div className="q-title">{title}</div>
      <div className="q-sub">Due soon</div>
    </div>

    <div className="q-right">
      <span className="q-value">{value}</span>
      <span className={`tag ${danger ? "danger" : ""}`}>{tag}</span>
    </div>
  </div>
);

type OverrideRowProps = {
  name: string;
  desc: string;
  value: string;
};

const OverrideRow = ({ name, desc, value }: OverrideRowProps) => (
  <div className="override-row">
    <div className="avatar">{name[0]}</div>

    <div className="o-content">
      <div>{name}</div>
      <div className="sub">{desc}</div>
    </div>

    <div className="o-value">{value}</div>
  </div>
);

type AuditRowProps = {
  time: string;
  auditType: string;
  text: string;
};

const AuditRow = ({ time, auditType, text }: AuditRowProps) => (
  <div className="audit-row">
    <span className="audit-time">{time}</span>
    <span className="audit-type">{auditType}</span>
    <span className="audit-text">{text}</span>
  </div>
);


  return (
    <div className="setup-screen pad-0 bg-transparent">
      <div className="dashboard">
        <div className="dashboard-kpi-grid">
          <KpiCard title="Pending Approvals" value="1,204" status="warning" icon={ClipboardList} />
          <KpiCard title="Avg. Confidence" value="92.6%" status="success" icon={Settings} />
          <KpiCard title="Human Overrides (30d)" value="318" icon={Users}/>
          <KpiCard title="Audit Events (24h)" value="4,182" icon={FileText} />
        </div>

        {/*  ROW 1 */}
        <div className="grid-2">
          <SpendTrendChart />

          <div className="panel-card">
            <div className="border-bottom pad-bottom-10 mar-bottom-10">
              <div className="dashboard-panel-title">Review queues</div>
              <div className="dashboard-panel-sub">Tasks awaiting human action.</div>
            </div>
            <QueueRow title="Classification — low confidence" value={412} tag="Open" />
            <QueueRow title="Supplier mappings — review" value={318} tag="Open" />
            <QueueRow title="Cleaning exceptions — currency" value={142} tag="SLA breach" danger />
            <QueueRow title="Workflow approval — Publish FY26" value={1} tag="Open" />
          </div>

        </div>

        {/*  ROW 2 */}
        <div className="grid-2">
          <div className="panel-card">
            <div className="border-bottom pad-bottom-10 mar-bottom-10">
              <div className="dashboard-panel-title">Human override tracking</div>
              <div className="dashboard-panel-sub">Recent overrides to AI decisions.</div>
            </div>
            <OverrideRow name="Marcus L." desc="Reclassified 12 rows in IT > SaaS" value="$184K" />
            <OverrideRow name="Ananya R." desc="Merged 8 duplicate supplier records" value="$2.1M" />
            <OverrideRow name="Sofia W." desc="Approved bulk consolidation rule" value="$640K" />
            <OverrideRow name="Marcus L." desc="Rejected AI mapping" value="—" />
          </div>


          <div className="panel-card">
            <div className="border-bottom pad-bottom-10 mar-bottom-10">
              <div className="dashboard-panel-title">Audit log</div>
              <div className="dashboard-panel-sub">Latest workflow events.</div>
            </div>

            <AuditRow time="12:08:18" auditType="auth.login" text="ananya.r@evaluserve.com"  />
            <AuditRow time="12:07:42" auditType="classify.Spanish" text="project=FY26 · rows=48,214" />
            <AuditRow time="11:58:09" auditType="supplier.merge" text="Acme Logistic → Acme Logistics..." />
            <AuditRow time="11:42:13" auditType="cleaning.bulk_approve" text="rules=currency_at=match ..." />
            <AuditRow time="12:08:18" auditType="ingestion.sync" text="connector=SAP · rows=21K"  />
            <AuditRow time="12:08:18" auditType="taxonomy.upload" text="UNSPSC v25.1"  />
            <AuditRow time="12:08:18" auditType="project.create" text="Coca-Cola Global Spend FY26"  />
          </div>

        </div>

      </div>
    </div>
  );
}
